import React, { useState } from 'react';
import {
  Typography,
  Box,
  Paper,
  TextField,
  Button,
  CircularProgress,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  LinearProgress,
  Stepper,
  Step,
  StepLabel,
  MenuItem
} from '@material-ui/core';
import TopMenu from '../TopMenu';
import CandidateAnalysisService from '../../services/CandidateAnalysisService';
import swal from 'sweetalert';
import './CandidateAnalysis.css';
import HomeIcon from '@material-ui/icons/Home';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import firebase from '../../config';

// Utility function to export analysis results
const exportAnalysisResults = (analysis) => {
  const reportData = {
    ...analysis,
    generatedAt: new Date().toISOString()
  };

  const blob = new Blob([JSON.stringify(reportData, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `candidate-analysis-${analysis.candidateEmail?.replace(/[^a-zA-Z0-9]/g, '-')}-${new Date().toISOString().split('T')[0]}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
  
  swal({
    title: 'Export Successful',
    text: 'Analysis results have been downloaded',
    icon: 'success',
    timer: 2000
  });
};

// Categorize questions based on section title and content according to analysis rules
const categorizeQuestionBySection = (sectionTitle, questionText, question) => {
  const title = (sectionTitle || '').toLowerCase();
  const text = (questionText || '').toLowerCase();
  
  // Determine exam type based on section title
  if (title.includes('psychometric') || title.includes('behavioral') || title.includes('personality') || title.includes('pst')) {
    // Psychometric Test - 4 specific competencies
    if (text.includes('problem') && text.includes('solve') && text.includes('logical')) {
      return { type: 'Psychometric', competency: 'ProblemSolving_Logical' };
    } else if (text.includes('learn') || text.includes('study') || text.includes('knowledge') || text.includes('explore') || text.includes('understand')) {
      return { type: 'Psychometric', competency: 'LearningOrientation' };
    } else if (text.includes('collaborat') || text.includes('communicat') || text.includes('team') || text.includes('help') || text.includes('support') || text.includes('work together')) {
      return { type: 'Psychometric', competency: 'Collaboration_Coachability_Communication' };
    } else if (text.includes('ai') || text.includes('artificial') || text.includes('tech') || text.includes('innovation') || text.includes('digital') || text.includes('automation')) {
      return { type: 'Psychometric', competency: 'AI_Orientation' };
    } else {
      // Default to Collaboration_Coachability_Communication for psychometric questions
      return { type: 'Psychometric', competency: 'Collaboration_Coachability_Communication' };
    }
  } else if (title.includes('logical') || title.includes('reasoning') || title.includes('analytical') || title.includes('lr')) {
    // Logical Reasoning Test - 3 specific competencies
    if (text.includes('problem') && text.includes('solve') || text.includes('analyze') || text.includes('find solution')) {
      return { type: 'Logical', competency: 'ProblemSolving' };
    } else if (text.includes('process') || text.includes('step') || text.includes('method') || text.includes('procedure') || text.includes('workflow')) {
      return { type: 'Logical', competency: 'Process' };
    } else if (text.includes('product') || text.includes('user') || text.includes('customer') || text.includes('market') || text.includes('business')) {
      return { type: 'Logical', competency: 'Product' };
    } else {
      // Default to ProblemSolving for logical reasoning questions
      return { type: 'Logical', competency: 'ProblemSolving' };
    }
  } else if (title.includes('coding') || title.includes('programming') || title.includes('technical') || title.includes('java') || title.includes('mcq') || title.includes('review')) {
    // MCQ Test - 5 specific competencies (technical knowledge)
    if (text.includes('java') || text.includes('programming') || text.includes('code') || text.includes('output') || text.includes('print') || text.includes('compile') || text.includes('exception') || text.includes('runtime') || text.includes('string') || text.includes('class') || text.includes('method')) {
      return { type: 'MCQ', competency: 'Java' };
    } else if (text.includes('algorithm') || text.includes('data structure') || text.includes('complexity') || text.includes('sort') || text.includes('search') || text.includes('tree') || text.includes('graph') || text.includes('array') || text.includes('linked list')) {
      return { type: 'MCQ', competency: 'DSA' };
    } else if (text.includes('sql') || text.includes('database') || text.includes('query') || text.includes('select') || text.includes('join') || text.includes('table') || text.includes('schema')) {
      return { type: 'MCQ', competency: 'SQL' };
    } else if (text.includes('oop') || text.includes('object') || text.includes('class') || text.includes('inheritance') || text.includes('polymorphism') || text.includes('encapsulation') || text.includes('abstraction') || text.includes('interface') || text.includes('abstract')) {
      return { type: 'MCQ', competency: 'OOPs' };
    } else {
      // Default to Others for other technical questions
      return { type: 'MCQ', competency: 'Others' };
    }
  } else {
    // If section title doesn't match any pattern, try to infer from question content
    return categorizeQuestionByContent(text);
  }
};

// Categorize questions based on content when section title is not clear
const categorizeQuestionByContent = (questionText) => {
  const text = questionText.toLowerCase();
  
  // Check for psychometric indicators
  if (text.includes('help') || text.includes('collaborat') || text.includes('communicat') || text.includes('team') || 
      text.includes('learn') || text.includes('study') || text.includes('ai') || text.includes('artificial') ||
      text.includes('support') || text.includes('work together') || text.includes('explore') || text.includes('understand')) {
    return { type: 'Psychometric', competency: 'Collaboration_Coachability_Communication' };
  }
  
  // Check for logical reasoning indicators
  if (text.includes('problem') && text.includes('solve') || text.includes('process') || text.includes('product') ||
      text.includes('analyze') || text.includes('find solution') || text.includes('method') || text.includes('procedure')) {
    return { type: 'Logical', competency: 'ProblemSolving' };
  }
  
  // Check for technical/coding indicators
  if (text.includes('java') || text.includes('programming') || text.includes('code') || text.includes('output') || 
      text.includes('compile') || text.includes('exception') || text.includes('runtime') || text.includes('print') ||
      text.includes('string') || text.includes('class') || text.includes('method') || text.includes('algorithm') ||
      text.includes('data structure') || text.includes('oop') || text.includes('object') || text.includes('inheritance')) {
    return { type: 'MCQ', competency: 'Java' };
  }
  
  // Default fallback
  return { type: 'MCQ', competency: 'Others' };
};

const CandidateAnalysis = () => {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('TSE');
  const [examCodes, setExamCodes] = useState([]); // Keep as empty array
  const [examCodesInput, setExamCodesInput] = useState(''); // Add new state for raw input
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [error, setError] = useState('');
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [currentStage, setCurrentStage] = useState(0);
  const [stageProgress, setStageProgress] = useState({});

  const analysisStages = [
    'Validating Input Parameters',
    'Fetching Assessment Data', 
    'Processing Question Responses',
    'Calculating Competency Scores',
    'Generating AI Analysis',
    'Compiling Final Report'
  ];

  // Add function to parse exam codes from input
  const parseExamCodes = (inputText) => {
    if (!inputText.trim()) return [];
    const codes = inputText.split(',').map(code => code.trim()).filter(code => code);
    return codes;
  };

  // Add the missing updateExamCode function
  const updateExamCode = (index, value) => {
    const newExamCodes = [...examCodes];
    newExamCodes[index] = value;
    setExamCodes(newExamCodes);
  };

  const updateStageProgress = (stage, status, message = '') => {
    setStageProgress(prev => ({
      ...prev,
      [stage]: { status, message, timestamp: new Date().toISOString() }
    }));
    
    if (status === 'completed') {
      setCurrentStage(stage + 1);
    }
  };

  const resetProgress = () => {
    setCurrentStage(0);
    setStageProgress({});
  };

  const handleAnalysis = async () => {
    if (!email.trim()) {
      setError('Please enter a valid email address');
      return;
    }

    // Parse exam codes from input
    const parsedCodes = parseExamCodes(examCodesInput);
    
    if (parsedCodes.length === 0) {
      setError('Please enter at least one exam code');
      return;
    }

    if (parsedCodes.length > 3) {
      setError('Maximum 3 exam codes are allowed per analysis');
      return;
    }

    setLoading(true);
    setError('');
    setAnalysis(null);
    setShowProgressModal(true);
    resetProgress();

    try {
      // Stage 1: Validating Input Parameters
      updateStageProgress(0, 'in-progress', 'Checking email and exam codes...');
      await new Promise(resolve => setTimeout(resolve, 500)); // Brief delay for UX
      
      // Process email: replace dots with commas ONLY for Firebase search
      const processedEmail = email.trim().replace(/\./g, ',');
      // console.log('Original email (for display):', email.trim());
      // console.log('Processed email (for Firebase):', processedEmail);
      
      updateStageProgress(0, 'completed', `Validated ${parsedCodes.length} exam codes (limit: 3)`);

      // Stage 2: Fetching Assessment Data
      updateStageProgress(1, 'in-progress', 'Retrieving assessment data from database...');
      
      // Stage 3: Processing Question Responses  
      updateStageProgress(2, 'in-progress', 'Analyzing question responses...');
      
      // Stage 4: Calculating Competency Scores
      updateStageProgress(3, 'in-progress', 'Computing competency-wise scores...');
      
      // Stage 5: Generating AI Analysis
      updateStageProgress(4, 'in-progress', 'Running AI evaluation analysis...');

      // Use the processed email for Firebase search, but keep original for display
      const result = await CandidateAnalysisService.analyzeCandidate(processedEmail, role, parsedCodes);
      
      // Mark stages as completed
      updateStageProgress(1, 'completed', 'Assessment data retrieved successfully');
      updateStageProgress(2, 'completed', 'Question responses processed');
      updateStageProgress(3, 'completed', 'Competency scores calculated');
      updateStageProgress(4, 'completed', 'AI analysis generated');
      
      // Stage 6: Compiling Final Report
      updateStageProgress(5, 'in-progress', 'Compiling final analysis report...');
      await new Promise(resolve => setTimeout(resolve, 800)); // Brief delay for final compilation
      updateStageProgress(5, 'completed', 'Analysis report ready');
      
      setAnalysis(result);
      
      // Update the success message
      setTimeout(() => {
        setShowProgressModal(false);
        swal({
          title: 'Profile Generation Complete',
          text: 'Candidate profile has been generated successfully',
          icon: 'success',
          timer: 2000
        });
      }, 1000);
      
    } catch (error) {
      console.error('Analysis error:', error);
      setError(error.message || 'Failed to analyze candidate. Please try again.');
      setShowProgressModal(false);
      
      // Update the error message
      swal({
        title: 'Profile Generation Failed',
        text: error.message || 'Failed to generate candidate profile',
        icon: 'error',
        timer: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  // Basic table-based results display
  const renderAnalysisResults = (analysis) => {
    if (!analysis) return null;

    // Helper function to get percentage class
    const getPercentageClass = (percentage) => {
      if (percentage >= 90) return 'percentage-excellent';
      if (percentage >= 80) return 'percentage-good';
      if (percentage >= 70) return 'percentage-average';
      if (percentage >= 50) return 'percentage-poor';
      return 'percentage-very-poor';
    };

    // Helper function to get level class
    const getLevelClass = (level) => `level-${level}`;

    return (
      <div className="analysis-results">
        {/* Basic Header */}

        {/* 1. Candidate Details */}
        <div className="section">
          <h2>1. Candidate Details</h2>
          <table className="basic-table">
            <tbody>
              <tr>
                <td><strong>Name</strong></td>
                <td>{analysis.candidateName}</td>
              </tr>
              <tr>
                <td><strong>Email</strong></td>
                <td>{analysis.candidateEmail}</td>
              </tr>
              <tr>
                <td><strong>Role</strong></td>
                <td>{analysis.role}</td>
              </tr>
              <tr>
                <td><strong>Exam Codes</strong></td>
                <td>{analysis.examCodes?.join(', ')}</td>
              </tr>
              <tr>
                <td><strong>Total Assessments</strong></td>
                <td>{analysis.totalAssessments}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* 2. MCQ Scores & Interpretation */}
        <div className="section">
          <h2>2. MCQ Scores & Interpretation</h2>
          <table className="basic-table">
            <thead>
              <tr>
                <th>Subject</th>
                <th>Score</th>
                <th>Percentage</th>
                <th>Level</th>
                <th>Interpretation</th>
              </tr>
            </thead>
            <tbody>
              {analysis.mcqScores?.map((score, index) => (
                <tr key={index}>
                  <td className="competency-name">{score.subCompetency}</td>
                  <td><span className="score-text">{score.raw}</span></td>
                  <td><span className={getPercentageClass(score.percentage)}>{score.percentage}%</span></td>
                  <td><span className={getLevelClass(score.readinessLevel)}>Level {score.readinessLevel}</span></td>
                  <td className="interpretation-text">{analysis.mcqInterpretations?.[score.subCompetency] || score.interpretation}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* 3. Coding Review Scores & Interpretation */}
        <div className="section">
          <h2>3. Coding Review Scores & Interpretation</h2>
          <table className="basic-table">
            <thead>
              <tr>
                <th>Area</th>
                <th>Score</th>
                <th>Percentage</th>
                <th>Level</th>
                <th>Interpretation</th>
              </tr>
            </thead>
            <tbody>
              {analysis.logicalScores?.map((score, index) => (
                <tr key={index}>
                  <td className="competency-name">{score.subCompetency}</td>
                  <td><span className="score-text">{score.raw}</span></td>
                  <td><span className={getPercentageClass(score.percentage)}>{score.percentage}%</span></td>
                  <td><span className={getLevelClass(score.readinessLevel)}>Level {score.readinessLevel}</span></td>
                  <td className="interpretation-text">{analysis.codingReviewInterpretation || score.interpretation}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* 4. Psychometric Results */}
        <div className="section">
          <h2>4. Psychometric Results</h2>
          <table className="basic-table">
            <thead>
              <tr>
                <th>Competency</th>
                <th>Raw</th>
                <th>%</th>
                <th>Level</th>
                <th>What This Means</th>
                <th>Interview Probes</th>
              </tr>
            </thead>
            <tbody>
              {analysis.psychometricResults?.map((result, index) => (
                <tr key={index}>
                  <td className="competency-name">{result.competency}</td>
                  <td><span className="score-text">{result.raw}</span></td>
                  <td><span className={getPercentageClass(result.percentage)}>{result.percentage}%</span></td>
                  <td><span className={getLevelClass(result.level)}>Level {result.level}</span></td>
                  <td className="interpretation-text">{result.whatThisMeans}</td>
                  <td>
                    <ul>
                      {result.interviewProbes?.map((question, qIndex) => (
                        <li key={qIndex}>{question}</li>
                      ))}
                    </ul>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* 5. Strengths & Focus Areas */}
        <div className="section">
          <h2>5. Strengths & Focus Areas</h2>
          <table className="basic-table">
            <thead>
              <tr>
                <th>Strengths</th>
                <th>Focus Areas to Probe</th>
                <th>Training Ideas</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="interpretation-text">{analysis.strengthsAndFocus?.strengths || 'Developing across multiple areas'}</td>
                <td className="interpretation-text">{analysis.strengthsAndFocus?.focusAreasToProbe || 'Continue building on current strengths'}</td>
                <td className="interpretation-text">{analysis.strengthsAndFocus?.trainingIdeas || 'Structured competency development programs'}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* 6. Suggested Interview Questions */}
        <div className="section">
          <h2>6. Suggested Interview Questions</h2>
          <table className="basic-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Question</th>
              </tr>
            </thead>
            <tbody>
              {analysis.suggestedInterviewQuestions?.map((question, index) => (
                <tr key={index}>
                  <td style={{ textAlign: 'center', fontWeight: '600', color: '#2c3e50' }}>{index + 1}</td>
                  <td className="interpretation-text">{question}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* 7. Overall Candidate Summary */}
        <div className="section">
          <h2>7. Overall Candidate Profiling</h2>
          <div className="summary-content">
            <p>{analysis.overallSummary || 'Candidate profiling summary will be generated here.'}</p>
          </div>
        </div>
      </div>
    );
  };

  // Update the form section to use single input with comma-separated validation
  const renderAnalysisForm = () => {
    return (
      <div className="analysis-form">
        <h2>Candidate Profiling</h2>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="email">Candidate Email:</label>
            <TextField
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter candidate email"
              fullWidth
              variant="outlined"
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="role">Role:</label>
            <TextField
              id="role"
              select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              fullWidth
              variant="outlined"
            >
              <MenuItem value="TSE">TSE</MenuItem>
            </TextField>
          </div>
        </div>
        
        <div className="form-row">
          <div className="form-group" style={{ flex: 2 }}>
            <label htmlFor="examCodes">Exam Codes (Comma Separated):</label>
            <TextField
              id="examCodes"
              value={examCodesInput}
              onChange={(e) => {
                setExamCodesInput(e.target.value);
              }}
              placeholder="Enter 3 exam codes separated by commas (e.g., GSCSA001, GSCSA002, GSCSA003)"
              fullWidth
              variant="outlined"
              required
              helperText={(() => {
                const parsedCodes = parseExamCodes(examCodesInput);
                const codesCount = parsedCodes.length;
                if (codesCount === 0) {
                  return "Enter 3 exam codes separated by commas";
                } else if (codesCount < 3) {
                  return `Enter ${3 - codesCount} more exam code(s). Example: GSCSA001, GSCSA002, GSCSA003`;
                } else if (codesCount === 3) {
                  return "✅ All 3 exam codes entered correctly";
                } else {
                  return `❌ Too many codes (${codesCount}/3). Please enter exactly 3 codes.`;
                }
              })()}
              error={(() => {
                const parsedCodes = parseExamCodes(examCodesInput);
                return parsedCodes.length > 3;
              })()}
            />
          </div>
        </div>
        
        <div className="form-row">
          <Button
            className="analyze-button"
            variant="contained"
            color="primary"
            onClick={handleAnalysis}
            disabled={loading || !email || !role || parseExamCodes(examCodesInput).length !== 3}
            fullWidth
          >
            {loading ? <CircularProgress size={24} /> : 'Generate Candidate Profile'}
          </Button>
        </div>
      </div>
    );
  };

  // Update the progress modal to remove all icon references
  const renderProgressModal = () => {
    return (
      <Dialog open={showProgressModal} maxWidth="sm" fullWidth>
        <DialogTitle>Generating Candidate Profile</DialogTitle>
        <DialogContent className="progress-modal">
          <LinearProgress />
          <div className="progress-stages">
            {analysisStages.map((stage, index) => {
              const stageData = stageProgress[index];
              const isActive = index === currentStage;
              const isCompleted = stageData?.status === 'completed';
              const isInProgress = stageData?.status === 'in-progress';

              return (
                <div key={stage} className={`stage ${stageData?.status}`}>
                  {stage}
                  {isCompleted && <span style={{ color: '#4caf50', marginLeft: '8px' }}>✓</span>}
                  {isInProgress && <CircularProgress size={16} style={{ color: '#1976d2', marginLeft: '8px' }} />}
                </div>
              );
            })}
          </div>
          
          {currentStage >= analysisStages.length && (
            <div style={{ textAlign: 'center', marginTop: '20px' }}>
              <div style={{ color: '#4caf50', fontSize: '48px', marginBottom: '8px' }}>✓</div>
              <Typography variant="h6" style={{ color: '#4caf50', fontWeight: 'bold' }}>
                Profile Generation Complete!
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Preparing your comprehensive candidate profile...
              </Typography>
            </div>
          )}
        </DialogContent>
      </Dialog>
    );
  };

  return (
    <div className="candidate-analysis-container">
      <TopMenu showHome showLogout />
      <div className="candidate-analysis-header">
        <Typography variant="h4" style={{ fontSize: '24px' }}>
          Candidate Profiling Dashboard
        </Typography>
      </div>
      
      {/* Add same header buttons as admin-dashboard */}
      
      
      {renderAnalysisForm()}
      
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}
      
      {renderProgressModal()}
      
      {analysis && renderAnalysisResults(analysis)}
    </div>
  );
};

export default CandidateAnalysis; 